import isEqual from 'lodash/isEqual'
import { all, call, CallEffect, put, select } from 'redux-saga/effects'

import { calculateNewDrawableLocations } from './calculateDrawableLocationsFromNewGroups'
import drawShapeByType from './drawShapeByType'
import { getJoistLinesByGroupId } from './getJoistLinesByGroupId'
import { RedrawShapeStoreState, selectRedrawState } from './handleUpdateOpeningGroupsSuccess'
import { setActiveDrawableGroup, updateJoistLinesSuccess } from '../../../actions/drawable'
import { OpeningAPI, OpeningGroupAPI } from '../../../models/activeDrawable'
import { ActiveFloor } from '../../../models/activeFloor'
import { DRAWING_TYPES } from '../../../shared/constants/drawable-types'
import { isNonAreaJoistLine } from '../../../utils/project/project-helper-functions'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Workspace } from '../../lib/toolBoxes/2D'
import addTooltipsToPath from '../../lib/utils/functionality-bindings/addTooltipsToPath'
import { setDrawableLocations } from '../../slices/2D'
import { updateToolbarMessage } from '../../slices/common'
import { selectDrawableActiveFloor } from '../../slices/documents'
import { ActiveGeometryGroup, GeometricDrawable, selectActiveGeometryGroup } from '../../slices/geometry'
import { selectIsConflictResolutionInProgress } from '../../slices/masterSetPlan'
import { IMUP2DDrawableLocation } from '../../types'

/**
 * The joist lines response holds only the regenerated openings of the group. handleSetActiveDrawableGroup2D
 * locks every canvas item whose opening is not listed in the activated group, so activating the group from
 * the response alone would lock the untouched openings. Take those from the group that is active now.
 */
export const withUntouchedOpenings = (
    response: OpeningGroupAPI,
    activeGroup: ActiveGeometryGroup | null
): Omit<OpeningGroupAPI, 'openings'> & { openings: (OpeningAPI | GeometricDrawable)[] } => {
    if (!activeGroup || activeGroup.id !== response.id) return response

    const returnedOpeningIds = new Set(response.openings.map((opening) => opening.id))
    const untouchedOpenings: GeometricDrawable[] = activeGroup.openings.filter(
        (opening) => !returnedOpeningIds.has(opening.id)
    )

    return untouchedOpenings.length ? { ...response, openings: [...response.openings, ...untouchedOpenings] } : response
}

export function* redrawJoistLinesOnUpdate(action: ReturnType<typeof updateJoistLinesSuccess>) {
    try {
        const manager: PaperManager | null = yield call(managers.get2DManager)

        if (!manager) return

        const newJoistGroup = action.payload.res

        const [workspaceTool] = yield call(manager.getTools, [Workspace.NAME])

        const { areaOpacity, lineOpacity }: RedrawShapeStoreState = yield select(selectRedrawState)

        const currentDrawableGroup: ActiveGeometryGroup | null = yield select(selectActiveGeometryGroup)

        const {
            additionalDrawableLocations,
            newDrawableLocations,
        }: { additionalDrawableLocations: IMUP2DDrawableLocation[]; newDrawableLocations: IMUP2DDrawableLocation[] } =
            yield call(calculateNewDrawableLocations, [newJoistGroup])

        // We've updated the joist's drawable locations -- we need to save them in the store's 2D slice.
        yield put(setDrawableLocations(newDrawableLocations))

        if (!currentDrawableGroup) return

        const currentGroupItems: paper.Item[] = yield call(
            workspaceTool.getItemsWithCriteria,
            'data',
            (data: any) => data.opening_group_id === newJoistGroup.id
        )
        // The endpoint regenerates and returns only the requested openings of the group (BLUEPRINTS-23741);
        // the other openings keep their lines, so only the joists of the returned openings are replaced.
        const returnedOpeningIds = new Set(newJoistGroup.openings.map((opening) => opening.id))
        const currentJoists = currentGroupItems.filter(
            (item) =>
                item.data.shapeType !== DRAWING_TYPES.AREA &&
                !item.data.materialModificationConflict &&
                returnedOpeningIds.has(item.data.drawable_id)
        )
        const newlyCreatedMaterialVisibility = new Map<number, boolean>(
            currentGroupItems
                .filter((item) => item.data.createdDuringConflictResolution && !item.data.materialModificationConflict)
                .map((item) => [item.data.drawable_id, item.visible])
        )

        // Remove the current joists from the canvas -- will be replaced below
        yield all(currentJoists.map((joist) => call([joist, 'remove'])))

        const activeFloor: ActiveFloor = yield select(selectDrawableActiveFloor)
        const sourceAreas = currentGroupItems.filter(
            (item) =>
                item.data.shapeType === DRAWING_TYPES.AREA &&
                item.data.document_chunk_id === activeFloor.document_chunk.id
        )
        // Generated joists can receive new drawable IDs. Inherit provenance only
        // when every source area belongs to a material created after Save.
        const areAllSourceAreasNew =
            sourceAreas.length > 0 &&
            sourceAreas.every(
                (item) => item.data.createdDuringConflictResolution && !item.data.materialModificationConflict
            )
        const areSourceAreasVisible = sourceAreas.every((item) => item.visible)

        const regionPaths: paper.Path[] = yield call(workspaceTool.getAllRegionItems)

        const paths: paper.Path[] = yield all(
            additionalDrawableLocations.reduce((paths, location) => {
                // Ignore the joist area which is not deleted and redrawn
                if (
                    isEqual(location.document_chunk_id, activeFloor.document_chunk.id) &&
                    location.shapeType !== DRAWING_TYPES.AREA
                ) {
                    return [...paths, call(drawShapeByType, location, areaOpacity, lineOpacity, regionPaths)]
                }

                return paths
            }, [] as CallEffect<paper.Path | null>[])
        )

        yield all(paths.map((path) => call(addTooltipsToPath, path, workspaceTool)))

        // Restore joist visibility according to the selected openings
        const newJoistLines: paper.Item[] = yield call(getJoistLinesByGroupId, workspaceTool, newJoistGroup.id)

        const isConflictResolutionInProgress: boolean = yield select(selectIsConflictResolutionInProgress)

        const activeOpeningIds = currentDrawableGroup.openings.reduce<number[]>((ids, opening) => {
            if (opening.isActive) ids.push(opening.id)

            return ids
        }, [])
        const activeOpeningIdsSet = new Set(activeOpeningIds)

        yield all(
            newJoistLines
                .filter((item) => !item.data.materialModificationConflict)
                .map((joistLine) =>
                    call((item: paper.Item) => {
                        const wasCreatedDuringConflicts =
                            newlyCreatedMaterialVisibility.has(item.data.drawable_id) || areAllSourceAreasNew

                        if (isConflictResolutionInProgress && wasCreatedDuringConflicts) {
                            item.data.createdDuringConflictResolution = true
                            const path = paths.find((path) => path === item || path.parent === item)

                            if (path) path.data.createdDuringConflictResolution = true

                            item.visible =
                                newlyCreatedMaterialVisibility.get(item.data.drawable_id) ?? areSourceAreasVisible
                        } else {
                            item.visible =
                                !isConflictResolutionInProgress &&
                                (!isNonAreaJoistLine(item) ||
                                    currentDrawableGroup.id !== newJoistGroup.id ||
                                    activeOpeningIdsSet.has(item.data.drawable_id))
                        }
                    }, joistLine)
                )
        )

        // we need set active drawable group from new joist to display joist lines; the untouched openings
        // of the group are added so that they are not locked on the canvas
        if (currentDrawableGroup) {
            yield put(
                setActiveDrawableGroup(
                    withUntouchedOpenings(newJoistGroup, currentDrawableGroup),
                    currentDrawableGroup.id === newJoistGroup.id ? activeOpeningIds : undefined
                )
            )
        }
    } catch (error) {
        yield call(console.error, error)
    } finally {
        // it's used in updateSingleDocumentMapping saga which trigger reset of active document chunk
        yield put(updateToolbarMessage(null))
    }
}
